import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';

// Mirrors VideoUploader but for MP3/audio tracks (used in the media library and
// the video editor). Posts to custom/v1/upload-audio (field name 'audio').
const AudioUploader = ({ uploadedAudio, setUploadedAudio }) => {
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const MAX_FILE_SIZE_MB = 50;

  const uploadAudio = async (file) => {
    setUploading(true);
    setMessage('');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('audio', file);

    try {
      const response = await axios.post(
        'https://api.schedpilot.com/wp-json/custom/v1/upload-audio',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / (e.total || 1))),
        }
      );

      if (response.data.code === 200) {
        setMessage('Upload successful!');
        setUploadedAudio((prev) => [
          ...prev,
          { url: response.data.file_url, id: response.data.audio_id, title: response.data.title },
        ]);
      } else {
        setMessage(response.data.message || 'Upload failed.');
      }
    } catch (error) {
      console.error(error);
      setMessage(error?.response?.data?.message || 'Error uploading audio. Please use an MP3.');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > MAX_FILE_SIZE_MB) {
        setMessage(`File "${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit.`);
        continue;
      }
      await uploadAudio(file);
    }
  }, [setUploadedAudio]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: { 'audio/*': ['.mp3', '.m4a', '.wav', '.aac', '.ogg'] },
  });

  return (
    <div style={{ border: '2px dashed #ccc', padding: 20, borderRadius: 8, backgroundColor: '#fff', marginTop: 20 }}>
      <div {...getRootProps()} style={{ cursor: 'pointer', padding: 30, textAlign: 'center' }}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the audio file(s) here ...</p>
        ) : (
          <p>Drag & drop MP3(s) here, or click to select files</p>
        )}
      </div>

      {uploading && (
        <div className="mt-2">
          <p>Uploading... {uploadProgress}%</p>
          <div style={{ height: 10, background: '#e0e0e0', borderRadius: 5 }}>
            <div style={{ width: `${uploadProgress}%`, background: '#4f46e5', height: '100%', borderRadius: 5, transition: 'width 0.2s' }} />
          </div>
        </div>
      )}

      {message && <p className={message.includes('successful') ? 'text-success mt-2' : 'text-danger mt-2'}>{message}</p>}

      {uploadedAudio.length > 0 && (
        <div className="mt-3 d-flex flex-column gap-2" style={{ backgroundColor: '#f2f2f2', padding: 10, borderRadius: 6 }}>
          {uploadedAudio.map((a, index) => (
            <div className="d-flex align-items-center gap-2" key={index}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title || 'Audio track'}</span>
              <audio src={a.url} controls style={{ height: 32, flex: 1 }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AudioUploader;
